import React, { useState, useEffect } from "react";
import { Avatar, Button, Input, List, Modal, Select, Tag } from "antd";
import { useTranslation } from "react-i18next";
import "./InviteTeamModal.css";
import downArrow from "../../assets/icons/chevron-down-invite.svg";
import { useGetStaffListOnboardQuery, useGetUserPrivilegeQuery, useOnboardingStaffsMutation, useUpdateStaffsPrivilegeMutation } from "../../service/apiSlices/dashboard";
import { RootState } from "../../service/store";
import { useSelector } from "react-redux";
import { showErrorToast, showSuccessToast } from "../../utilities/toastUtils";
import { getAvatarColor, getAvatarText, validateEmail } from "../../utilities/utils";

const InviteTeamModal = ({ visible, onClose }: any) => {
    const { t } = useTranslation("common");
    const [privilegeOptions, setPrivilegeOptions] = useState<{ value: number, label: string }[]>([]);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string | null>(null);

    // Fetch staff list and privilege data
    const { data: onboardStaffList, isLoading, error } = useGetStaffListOnboardQuery({ skip: !visible });
    const { data: userPrivilege } = useGetUserPrivilegeQuery({ skip: !visible });
    const [onboardingStaffs] = useOnboardingStaffsMutation();
    const [updateStaffsPrivilege] = useUpdateStaffsPrivilegeMutation();

    const currentProfileName = useSelector((state: RootState) => state?.static.profileName);
    const currentProfileImage = useSelector((state: RootState) => state?.static.profileImage);

    // Process privilege data for dropdown options when it's available
    useEffect(() => {
        if (userPrivilege?.data && Array.isArray(userPrivilege?.data)) {
            const options = userPrivilege?.data?.map((privilege: any) => ({
                value: privilege.id,
                label: privilege.privilage_name
            }));
            setPrivilegeOptions(options);
        }
    }, [userPrivilege]);

    // Create combined data when staff list changes
    useEffect(() => {
        if (onboardStaffList?.data) {
            // Create a local copy of staff data that we can modify
            const staffListCopy = JSON.parse(JSON.stringify(onboardStaffList.data));

            // Combine owner data with staff data
            const ownerData = {
                email: currentProfileName || "Owner",
                role: "Owner",
                isCurrentUser: true,
                profileImage: currentProfileImage
            };

            setStaffList([ownerData, ...staffListCopy]);
        } else {
            const ownerData = {
                email: currentProfileName || "Owner",
                role: "Owner",
                isCurrentUser: true,
                profileImage: currentProfileImage
            };
            setStaffList([ownerData]);
        }
    }, [onboardStaffList, currentProfileName, currentProfileImage]);

    const handlePrivilegeChange = (value: number, staffId: number, staffIndex: number) => {
        // Create a new copy of the staffList
        const updatedStaffList = [...staffList];
        // Find the staff member by index (faster than searching by ID)
        const staffMember = updatedStaffList[staffIndex];
        if (staffMember && !staffMember.isCurrentUser) {
            // Update the privilege array
            staffMember.privilage = [value];

            // Find the privilege name from options
            const selectedPrivilege = privilegeOptions.find(option => option.value === value);
            if (selectedPrivilege) {
                staffMember.privilage_name = [selectedPrivilege.label];
            }

            let payload = {
                email: staffMember?.email,
                privilege: staffMember?.privilage_name[0]
            }

            updateStaffsPrivilege(payload).unwrap()
                .then((response: any) => {
                    if (response?.status_code === 200) {
                        showSuccessToast(response.message);
                        setStaffList(updatedStaffList);
                    }
                }).catch((error: any) => {
                    showErrorToast('Failed to change permission');
                })
            // Update the state with the modified list
        }
    };

    const handleOnWheel = (e: any) => {
        e.stopPropagation();
    };

    // Handle email input change
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);

        if (!value) {
            setEmailError(t("inviteTeamModal.validation.emailRequired"));
        } else if (!validateEmail(value)) {
            setEmailError(t("inviteTeamModal.validation.invalidEmail"));
        } else {
            setEmailError(null);
        }
    };

    // Handle invite button click
    const handleInvite = () => {
        if (!email) {
            setEmailError(t("inviteTeamModal.validation.emailRequired"));
            return;
        }
        if (!validateEmail(email)) {
            setEmailError(t("inviteTeamModal.validation.invalidEmail"));
            return;
        }

        let payload = {
            email: email,
            role_name: "staff",
            privilage: "view-edit",
            organization_name: "W2S",
            first_name: "",
            last_name: ""
        };

        onboardingStaffs(payload).unwrap()
            .then((response: any) => {
                if (response?.status_code === 200) {
                    showSuccessToast(response.message);
                    setEmail("");
                    setEmailError(null);
                } else {
                    showErrorToast(response.message);
                    setEmail("");
                    setEmailError(null);
                }
            }).catch((error: any) => {
                showErrorToast('Failed to add staff');
            })
    };

    return (
        <Modal
            centered
            open={visible}
            onOk={onClose}
            onCancel={onClose}
            footer={null}
            width={450}
            closable
            maskClosable={false}
            className="invite-team-modal-container"
        >
            <div className="invite-team-modal" onWheel={handleOnWheel}>
                <div className="invite-team-modal-header">
                    <h4 className="invite-team-modal-title">{t("inviteTeamModal.title")}</h4>
                </div>
                <div className="invite-team-modal-body">
                    {/* <Segmented
                        options={[
                            { label: t("inviteTeamModal.addAdmin"), value: "admin" },
                            { label: t("inviteTeamModal.addStaffs"), value: "stafs" },
                        ]}
                        defaultValue="admin"
                        className="invite-team-modal-segmented"
                    /> */}
                    <div className="search-input-wrapper">
                        {/* <label className="invite-team-modal-email-label">{t("inviteTeamModal.add")}</label> */}
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <Input
                                type="email"
                                placeholder={t("inviteTeamModal.emailPlaceholder")}
                                className="invite-team-modal-input"
                                value={email}
                                onChange={handleEmailChange}
                                status={emailError ? "error" : ""}
                            />
                            <Button
                                onClick={handleInvite}
                                className="invite-team-modal-button"
                                disabled={!!emailError || !email} // Disable if there's an error or email is empty
                            >
                                {t("inviteTeamModal.buttonText")}
                            </Button>
                        </div>
                        {emailError && <div className="text-danger mt-1">{emailError}</div>}
                    </div>
                    <div className="user-list">
                        {isLoading ? (
                            <div>Loading...</div>
                        ) : error ? (
                            <div>Get Privilege From Admin</div>
                        ) : (
                            <List
                                itemLayout="horizontal"
                                dataSource={staffList}
                                renderItem={(item, index) => (
                                    <List.Item
                                        actions={
                                            !item.isCurrentUser && privilegeOptions.length > 0 ? [
                                                <Select
                                                    value={item.privilage ? item.privilage[0] : undefined}
                                                    className="userPermissionDropdown"
                                                    options={privilegeOptions}
                                                    onChange={(value) => handlePrivilegeChange(value, item.staff_id, index)}
                                                    suffixIcon={<img src={downArrow} alt="icon" className="down-arrow-user-access" />}
                                                    bordered={false}
                                                />
                                            ] : []
                                        }
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                item.profileImage ? (
                                                    <Avatar src={item.profileImage} />
                                                ) : (
                                                    <Avatar
                                                        style={{ backgroundColor: getAvatarColor(item.email) }}
                                                    >
                                                        {getAvatarText(item.email)}
                                                    </Avatar>
                                                )
                                            }
                                            title={
                                                <div className="d-flex justify-content-between">
                                                    <div>{item.email}</div>
                                                    {item.isCurrentUser ? (
                                                        <Tag className="border-0 bg-transparent text-dark me-0">
                                                            {item.role}
                                                        </Tag>
                                                    ) : (
                                                        item.privilage && item.privilage_name && item.privilage_name.length > 0 && (
                                                            <> {/* <span className="privilege-display">
                                                                {item.privilage_name[0]}
                                                            </span> */}</>
                                                        )
                                                    )}
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default InviteTeamModal;